import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * One output column. Like the v1 csv_transform column (name + type), plus a
 * per-column `mode` switch:
 *
 *  - `ai`      → the column is produced by the AI engine. `expression` is an
 *                optional natural-language hint for that column.
 *  - `formula` → the column is computed deterministically (no AI) by evaluating
 *                the Excel `expression` row-by-row. The formula is authored as
 *                it would appear in the first data row and references input
 *                columns by spreadsheet letter (A = first input column, B =
 *                second, …), e.g. `="BUR"&D2` or
 *                `=SUBSTITUTE(SUBSTITUTE(D2," ",""),"-","")`.
 *
 * The schema is a discriminated union on `mode` so that `expression` is
 * required (and non-empty) for formula columns and optional for AI columns.
 */
const columnBase = {
  name: z.string().min(1),
  type: z.enum(["string", "number", "date", "boolean"]),
};

export const csvTransformV4ColumnSchema = z.discriminatedUnion("mode", [
  z.object({
    ...columnBase,
    mode: z.literal("ai"),
    expression: z.string().optional(),
  }),
  z.object({
    ...columnBase,
    mode: z.literal("formula"),
    expression: z
      .string()
      .min(1, "Formula columns require a non-empty expression"),
  }),
]);

export type CsvTransformV4Column = z.infer<typeof csvTransformV4ColumnSchema>;

/**
 * Locates the real data table inside a raw file that has extra rows before
 * the header (e.g. document metadata) and/or after the data (e.g. a totals
 * row, footer text, terms & conditions). Applied once, deterministically,
 * before any formula/AI column is evaluated. `column` uses the same
 * spreadsheet-letter addressing as formula expressions (A = first input
 * column, B = second, …).
 */
const tableBoundsColumnMatch = {
  column: z
    .string()
    .min(1)
    .describe("Spreadsheet column letter to inspect, e.g. 'A'."),
  value: z.string().min(1),
};

export const csvTransformV4TableBoundsSchema = z.object({
  headerRow: z
    .object(tableBoundsColumnMatch)
    .describe(
      "Scans the raw rows (including the original first row) for the first one where `column` equals `value` (trimmed); that row's cells become the new headers, and everything before it is dropped.",
    ),
  stopRow: z
    .discriminatedUnion("mode", [
      z.object({ mode: z.literal("blankRow") }),
      z.object({ mode: z.literal("columnEquals"), ...tableBoundsColumnMatch }),
      z.object({
        mode: z.literal("columnStartsWith"),
        ...tableBoundsColumnMatch,
      }),
    ])
    .optional()
    .describe(
      "Truncates the data rows at the first row matching this rule (exclusive of that row). Use 'blankRow' to stop at the first fully-empty row (e.g. before a totals/footer section).",
    ),
});

export type CsvTransformV4TableBounds = z.infer<
  typeof csvTransformV4TableBoundsSchema
>;

export const csvTransformV4Step = defineStepConfig({
  type: "csv_transform_v4",
  name: "CSV/XLSX Transform (V4)",
  description:
    "CSV/XLSX transform with per-column control. Each output column is either produced by AI (free-text hint) or computed deterministically from an Excel formula. Deterministic columns are evaluated first and injected, then AI fills in the rest. Takes a tabular document and produces a CSV document.",
  category: "AI & Agents",

  inputSchema: z.object({
    inputDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Document containing the input CSV/XLSX",
    ),
  }),

  outputSchema: z.object({
    outputDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Document containing the transformed CSV",
    ),
  }),

  configSchema: z.object({
    tableBounds: csvTransformV4TableBoundsSchema
      .optional()
      .describe(
        "Optional deterministic preprocessing to crop the raw table to just the real header + data rows, before any column is computed.",
      ),
    columns: z
      .array(csvTransformV4ColumnSchema)
      .min(1)
      .describe("Output columns, in order. Each is AI- or formula-driven."),
    instructions: z
      .string()
      .optional()
      .describe("Optional freeform guidance applied to the AI-driven columns"),
    flattenMultiRowHeaders: z
      .boolean()
      .optional()
      .default(false)
      .describe(
        "Before running the AI transform, collapse boundary-detected multi-row headers (e.g. a metadata block or Excel-style category row above the real column header) into a single header row. Off by default — the metadata-vs-hierarchy detection is heuristic; enable per-source once validated.",
      ),
    dynamicInputs: z
      .array(
        z.object({
          name: z
            .string()
            .min(1)
            .regex(
              /^[a-zA-Z][a-zA-Z0-9_]*$/,
              "Must start with a letter and contain only letters, digits, or underscores",
            ),
          label: z.string().optional(),
          description: z.string().optional(),
        }),
      )
      .optional()
      .describe(
        "Extra scalar (string) input sockets wirable on the canvas. Available in formula columns as $$name and injected as context for AI columns.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
  },
});
